import React, { useState } from 'react';
import { Shuffle, Home } from 'lucide-react';

const DialogGame = () => {
  const [gameState, setGameState] = useState('setup');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [numPlayers, setNumPlayers] = useState(3);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [currentStatement, setCurrentStatement] = useState(null);
  const [positions, setPositions] = useState({});
  const [round, setRound] = useState(1);
  const [startMethod, setStartMethod] = useState(null);
  const [startingPlayerSelected, setStartingPlayerSelected] = useState(false);
  const [usedStatements, setUsedStatements] = useState({});
  const [discussedStatements, setDiscussedStatements] = useState([]);
  const [showArchive, setShowArchive] = useState(false);

  // --- NYTT FOR OPPVARMING ---
  const [remainingWarmups, setRemainingWarmups] = useState(0);
  const [originalCategory, setOriginalCategory] = useState(null);
  const [isWarmupPhase, setIsWarmupPhase] = useState(false);


  const categories = {
    'Oppvarming': {
      color: '#eab308',
      statements: [
        "En pølse i brød er teknisk sett en sandwich.",
        "Sokker i sandaler er et undervurdert motestatement.",
        "Det er greit å spise middagsrester til frokost.",
        "Man burde kunne velge sin egen bursdag.",
        "Spøkelser finnes, men de er bare veldig sjenerte.",
        "Ketchup passer til nesten all mat, inkludert pasta.",
        "Å se en hel TV-sesong på én dag er en imponerende bragd.",
        "Det er helt greit å snakke med seg selv offentlig.",
        "Frokostblanding smaker bedre om kvelden.",
        "Å dyppe pommes frites i milkshake er deilig.",
        "Verden hadde vært et bedre sted med flere hengekøyer.",
        "Superkrefter ville vært mer slitsomt enn gøy i hverdagen.",
        "Det er greit å velge film basert kun på plakaten.",
        "Å lese slutten på en bok først burde vært ulovlig.",
        "Brunost er Norges rareste, men beste, oppfinnelse.",
        "Tegneserier er like seriøs litteratur som romaner.",
        "Man burde få fri fra skolen dagen etter Halloween.",
        "Musikk uten tekst er kjedelig.",
        "Å kunne snakke med dyr er en bedre superkraft enn å kunne fly.",
        "Superheltfilmer er overvurdert.",
        "Katter er bedre enn hunder.",
        "Det er greit å spise dessert til frokost.",
        "Cornflakes er den kjedeligste frokostblandingen som finnes.",
        "Det er greit å heie på skurken i en film.",
        "Regn lukter bedre enn nystekt brød.",
        "Å ha en plante som kjæledyr er like givende som å ha en hund.",
        "Alle bøker burde ha en 'spoiler'-seksjon på første side."
      ]
    },
    'Vennskap': {
      color: '#3b82f6',
      statements: [
        "Det er bedre å ha få nære venner enn mange overfladiske venner.",
        "Man bør alltid være ærlig med vennene sine, selv om det kan såre dem.",
        "Det er greit å ikke invitere alle i vennegjengen til alt.",
        "Å 'ghoste' en venn er aldri greit.",
        "Man kan være bestevenn med noen man aldri har møtt fysisk.",
        "En 'ekte' bestevenn kan man bare ha én av.",
        "Vennskap på sosiale medier er like verdifulle som vennskap i virkeligheten.",
        "Det er greit å være sjalu på vennenes suksess.",
        "Man bør fortelle en venn hvis man ikke liker kjæresten deres.",
        "Hemmeligheter man deler i en vennegjeng, må bli i vennegjengen.",
        "Det er viktigere at en venn er morsom enn at en venn er lojal.",
        "Du bør si ifra til en venn om rykter du har hørt om dem.",
        "Man bør konfrontere en venn som baksnakker andre.",
        "Det er greit å bli venn med eksen til en venn.",
        "Å låne bort penger til venner skaper bare problemer.",
        "Hvis to av vennene dine krangler, bør du velge en side.",
        "Man vokser fra venner, og det er helt greit.",
        "Gutter og jenter kan ikke være 'bare' venner uten at følelser blandes inn.",
        "Det er teit å ha en 'snapstreak' som grunnlag for et vennskap.",
        "Å si 'jeg er glad i deg' til en venn av samme kjønn er ikke rart.",
        "En god venn husker alltid bursdagen din uten påminnelse.",
        "Man bør droppe en venn som er en dårlig påvirkning.",
        "Du bør forsvare en venn selv om du vet at de har feil.",
        "Det er greit å si at du er opptatt, selv om du bare vil være alene.",
        "En venns musikksmak sier mye om hvem de er.",
        "Det er rart å ikke ha en definert 'bestevenn'.",
        "Å dele hemmeligheter er det som gjør et vennskap sterkt."
      ]
    },
    'Skole og trivsel': {
      color: '#10b981',
      statements: [
        "Lekser burde avskaffes helt.",
        "Karakterer er viktigere enn å faktisk lære noe.",
        "Skoleuniform er en god idé for å redusere motepress.",
        "Muntlige presentasjoner er den verste formen for vurdering.",
        "Elevene bør få bestemme mer over hva de skal lære.",
        "Det er viktigere å være populær enn å være seg selv på skolen.",
        "Man lærer mer av gruppearbeid enn av å jobbe alene.",
        "Lærere bør alltid gripe inn når de ser mobbing.",
        "Det er greit å si ifra hvis noen blir mobbet, selv om det er 'sladring'.",
        "Å være den smarteste i klassen er flaut.",
        "Musikk på ørene i timen burde alltid vært lov.",
        "Skolen starter for tidlig om morgenen.",
        "Det er greit å ekskludere noen fra gruppen hvis de er annerledes.",
        "Gym er den viktigste timen på skolen.",
        "Skolen burde fokusere mer på praktiske ferdigheter som økonomi og matlaging.",
        "Å rekke opp hånden er skummelt, selv når man kan svaret.",
        "Man bør få lov til å bruke mobiltelefon i friminuttene.",
        "Det er lærerens feil hvis en time er kjedelig.",
        "Pugging er en unødvendig måte å lære på.",
        "Alle burde være med på klassekameratenes bursdagsfeiringer.",
        "Det er greit å si at man er syk for å unngå en prøve.",
        "Man lærer mer av å feile enn av å få alt riktig.",
        "Sitteplasser i klasserommet burde alltid være frie.",
        "Det er viktigere å være kreativ enn å være flink i matte.",
        "Man burde få velge gym-aktiviteter selv.",
        "Karakterfrie fag er bortkastet tid."
      ]
    },
    'Digital verden': {
      color: '#8b5cf6',
      statements: [
        "Å ekskludere noen fra en gruppechat med vilje er en form for mobbing.",
        "Det er din egen feil hvis du blir såret av anonyme kommentarer på nettet.",
        "Å dele et skjermbilde av en privat samtale er alltid et svik.",
        "Foreldre forstår ikke problemene og presset på sosiale medier.",
        "FOMO (Fear Of Missing Out) er et reelt problem skapt av Instagram og Snapchat.",
        "Algoritmer som velger hva du ser på TikTok og YouTube, er farligere enn folk tror.",
        "Det er greit å lage en falsk profil for å spionere på andre.",
        "Man blir bedømt ut fra hvor raskt man svarer på en melding.",
        "Å true med å dele private bilder av noen er en av de verste formene for mobbing.",
        "Det er umulig å være helt seg selv på sosiale medier.",
        "Sammenligningskulturen på Instagram gjør mer skade enn nytte.",
        "Å ikke få 'likes' på et bilde kan ødelegge dagen din.",
        "Anonyme hate-kommentarer bør alltid anmeldes til politiet.",
        "Sosiale medier gjør ungdom mer ensomme.",
        "Det er viktigere hvor mange likes man får enn hva man poster.",
        "Foreldre bør ha tilgang til alt barna gjør på mobilen.",
        "Man bør kunne være helt anonym på internett.",
        "Å ha en 'privat' og en 'offentlig' konto er uærlig.",
        "Influencere har et moralsk ansvar for produktene de reklamerer for.",
        "Det er greit å lyve om alderen sin på internett for å få tilgang til innhold.",
        "Gaming er en likeverdig sosial aktivitet som å møtes fysisk.",
        "Å slette gamle, flaue innlegg er det samme som å skjule fortiden sin.",
        "Man bør alltid spørre om lov før man legger ut bilder av venner.",
        "En 'digital detox' (pause fra skjerm) er nødvendig for alle.",
        "Falske nyheter er lett å oppdage hvis man bare tenker seg om.",
        "Det er rart å følge folk man ikke kjenner på sosiale medier.",
        "Å lese andres meldinger er verre enn å lese dagboken deres.",
        "Man blir avhengig av mobiltelefonen sin.",
        "Emoji-bruken din sier mye om hvem du er som person.",
        "Å se på andres 'stories' uten å følge dem er rart.",
        "Det er greit å blokkere noen uten å gi en forklaring.",
        "TikTok gjør mer skade enn nytte for unge.",
        "Folk presenterer en falsk versjon av seg selv på nett.",
        "KI (kunstig intelligens) er mer skummelt enn spennende.",
        "Det er greit å dele bilder av andre uten å spørre først.",
        "Det er greit å bruke KI til å hjelpe med skolearbeid.",
        "Folk som bruker mange emojier virker mindre seriøse."
      ]
    },
    'Ungdom og kropp': {
      color: '#ec4899',
      statements: [
        "Det er greit å ombestemme seg og si nei, selv om man først sa ja.",
        "Pornografi gir et skadelig og urealistisk bilde av sex.",
        "Skolens seksualundervisning er utdatert og for dårlig.",
        "Det er et stort press om å debutere seksuelt før man egentlig er klar.",
        "Man bør aldri dele passord til mobil eller sosiale medier med en kjæreste.",
        "Du har rett til å si nei til en klem, selv fra nære venner eller familie.",
        "Man har et ansvar for å si ifra hvis en venn er i et usunt forhold.",
        "Nakenbilder ('nudes') bør aldri sendes, risikoen er for stor.",
        "Det er viktigere å være tro mot egne følelser enn å unngå å såre noen.",
        "Det er normalt å være usikker på kroppen sin som ungdom.",
        "Det snakkes for lite om mental helse, spesielt blant gutter.",
        "Alle bør få lære om kropp og seksualitet på skolen.",
        "Det er viktigere å trene for å føle seg bra enn for å se bra ut.",
        "Man bør ikke få lov til å ta kosmetiske inngrep før man er 18.",
        "Jenter og gutter bør kunne være venner uten at det blir romantisk.",
        "Kjærlighetssorg er en overvurdert følelse.",
        "Det er greit å slå opp med noen via en tekstmelding.",
        "Forelskelse er mer forvirrende og slitsomt enn det er gøy.",
        "Man bør vente med kjærlighet til man er voksen.",
        "Det er viktig å snakke åpent om følelser.",
        "Klær og stil er en viktig del av identiteten din.",
        "Man bør alltid tørre å gi et kompliment hvis man tenker noe fint om noen.",
        "Helse handler like mye om det mentale som det fysiske.",
        "Det er greit å ikke ville ha kjæreste.",
        "Det er pinlig å snakke med foreldre om kropp og følelser.",
        "Første kyss er alltid mer kleint enn romantisk.",
        "Det er viktigere å være komfortabel enn å følge motetrender.",
        "Å snakke om følelser er et tegn på styrke, ikke svakhet.",
        "Man burde lære om mental førstehjelp på skolen.",
        "Det er greit å ville endre på utseendet sitt."
      ]
    },
    'Tro og verdier': {
      color: '#6366f1',
      statements: [
        "Det er aldri greit å lyve, selv for å beskytte noens følelser.",
        "Å finne noe på gaten og beholde det er det samme som å stjele.",
        "Det er viktigere å være lykkelig enn å være rik.",
        "Meningen med livet er å ha det gøy og oppleve mest mulig.",
        "Alle burde jobbe frivillig en eller annen gang i livet.",
        "Man bør respektere andres tro, selv om man er helt uenig.",
        "Det er greit å ikke tro på noen gud.",
        "Familie er alltid viktigere enn venner.",
        "Det er viktigere å være snill enn å følge regler.",
        "Man er selv fullt ut ansvarlig for sin egen lykke.",
        "Alle religioner lærer i bunn og grunn det samme.",
        "Verden er et rettferdig sted for det meste.",
        "Det er viktig å kunne tilgi, selv om man ikke kan glemme.",
        "Religion og vitenskap kan ikke være sanne samtidig.",
        "Barn bør få velge sin egen religion når de blir eldre.",
        "Å dømme folk basert på førsteinntrykket er naturlig og greit.",
        "Penger kan faktisk kjøpe lykke.",
        "Det er greit å sette sine egne behov foran andres.",
        "Tradisjoner er viktige og bør holdes ved like.",
        "Det finnes en klar forskjell på rett og galt i alle situasjoner.",
        "Å være ærlig er viktigere enn å være populær.",
        "Hevn kan noen ganger være rettferdig.",
        "Mennesker er i bunn og grunn egoistiske.",
        "Det er viktigere å følge loven enn sin egen samvittighet.",
        "Astrologi og stjernetegn har noe for seg.",
        "Det er umulig å være en helt god person hele tiden.",
        "Religion har forårsaket mer krig og konflikt enn fred i verden.",
        "Man bør alltid stille spørsmål ved det autoriteter (lærere, foreldre, ledere) sier.",
        "Tro uten bevis er en farlig ting.",
        "Det er respektløst å lage vitser om andres religion eller gud.",
        "Hvis en allmektig og god gud finnes, ville det ikke vært så mye lidelse i verden.",
        "En sterk følelse av at noe er sant, er like gyldig som vitenskapelige bevis.",
        "Hellige tekster, som Bibelen eller Koranen, bør ikke tolkes bokstavelig i dag.",
        "Å endre mening om en viktig sak er et tegn på svakhet.",
        "De fleste konspirasjonsteorier har en kjerne av sannhet.",
        "Religiøse ledere bør ikke ha noen form for politisk makt.",
        "Det er viktigere å tvile enn å tro.",
        "Folk som er helt skråsikre i sin sak, er de man bør stole minst på.",
        "Man har et ansvar for å aktivt oppsøke meninger man er uenig i.",
        "Vitenskapen vil til slutt kunne forklare alt, inkludert meningen med livet.",
        "Å leve et liv basert på 'karma' eller 'synd' gjør deg ikke nødvendigvis til et bedre menneske.",
        "Samfunnet hadde vært bedre uten noen form for religion."
      ]
    },
    'Et dukkehjem': {
      color: '#881337',
      statements: [
        "Det er negativt å skylde vennene sine penger i mer enn en dag.",
        "Det kan være irriterende om noen som er glad i deg, ofte bruker kjælenavn når de snakker til deg.",
        "Når en dame og en mann gifter seg og flytter sammen, bør mannen bestemme hvordan pengene i huset skal brukes.",
        "Kvinner med lite utdanning og liten formue bør gifte seg med en rik mann, selv om de elsker en annen.",
        "Løgn og uærlighet er egenskaper som går i arv fra generasjon til generasjon.",
        "Kjærester og ektefeller bør kunne ha store hemmeligheter for hverandre.",
        "Hvis man vet at en venn skjuler en alvorlig forbrytelse for kjæresten sin, bør man sørge for å informere kjæresten.",
        "Jenter bør oppdras på en annen måte enn gutter.",
        "Det er greit å flørte med andre selv om man er i et parforhold.",
        "Hvis man etter konfirmasjonen har jukset eller forfalsket underskrifter, bør man aldri få viktige lederstillinger.",
        "Hvis man får en dødelig sykdom, bør man trekke seg unna og avvise besøk fra nære venner.",
        "Av og til kan det være best for barna at moren deres flytter langt bort.",
        "Hva andre tenker om deg er viktigere enn hva du tenker om deg selv.",
        "En person som ikke tjener sine egne penger, kan aldri være helt fri.",
        "Det er viktigere å ha en jobb med høy status enn en jobb man trives i.",
        "I et forhold bør den som tjener mest penger ha mest å si om felles økonomi.",
        "Dagens skjønhetsidealer er like skadelige som korsettene var på 1800-tallet.",
        "Det er altfor lett å skille seg i dag.",
        "Foreldre i dag er altfor involvert i barnas liv.",
        "I dag har gutter og jenter helt like muligheter i Norge.",
        "Stor aldersforskjell i et parforhold er uproblematisk så lenge begge er voksne.",
        "Å ha et eksotisk kjæledyr er bare en måte å vise seg fram på."
      ]
    },
    'Miljø': {
      color: '#059669',
      statements: [
        "Enkeltpersoner kan ikke gjøre noen reell forskjell for klimaet.",
        "Det er urettferdig at unge må løse klimaproblemene de voksne har skapt.",
        "Kjøttfri mandag burde vært obligatorisk i alle hjem.",
        "Alle burde være vegetarianere for miljøets skyld.",
        "Å kjøpe brukte klær er alltid bedre enn å kjøpe nye.",
        "Det burde koste mye mer å fly.",
        "Teknologi vil løse klimakrisen for oss til slutt.",
        "Å pante flasker er en av de viktigste tingene man kan gjøre for miljøet.",
        "Norge gjør nok for å bekjempe klimaendringer.",
        "Det er greit å kjøre bil til skolen selv om man kan gå eller sykle.",
        "Forbruk er et større problem enn forurensning fra industrien.",
        "Unge mennesker bryr seg mer om miljøet enn voksne.",
        "Det er greit å fly på ferie selv om det skader miljøet.",
        "Skolen burde undervise mer om klima og miljø.",
        "Å sortere søppel er unødvendig, alt blir brent sammen uansett.",
        "Det er flaut å plukke opp søppel som andre har kastet.",
        "Vi bør forby salg av engangsplastprodukter helt.",
        "Å reise med tog er bedre enn å fly, uansett hvor lang tid det tar.",
        "Folk som er klimaaktivister overdriver ofte.",
        "Jeg er bekymret for fremtiden på grunn av klimaendringene.",
        "Det er viktigere å redde jobber enn å stenge ned forurensende fabrikker.",
        "Bedrifter bør straffes hardere for forurensing enn enkeltpersoner.",
        "El-sparkesykler er mer et problem enn en løsning.",
        "Klimaangst er et reelt og alvorlig problem.",
        "Å spise lokal mat er viktigere enn å spise vegetarisk.",
        "Det er hyklersk å være klimaaktivist og samtidig eie en smarttelefon."
      ]
    }
    ,
    'Bærekraftig utvikling': {
      color: '#f97316',
      statements: [
        "Det er greit å kjøpe billige klær fra sider som SHEIN og Temu, selv om det er dårlig for miljøet.",
        "Å følge motetrender er viktigere enn å tenke på hvor klærne kommer fra.",
        "Den eneste riktige måten å handle på er å kjøpe brukt.",
        "For å redde planeten, må vi rett og slett slutte å kjøpe ting vi ikke trenger.",
        "Bedrifter som sier de er 'miljøvennlige' prøver bare å lure oss til å kjøpe mer.",
        "Det er bedre at en stor, forurensende bedrift gjør én liten miljøvennlig ting, enn ingenting i det hele tatt.",
        "Verdens største problem er at vi blir for mange mennesker på jorda.",
        "Det er et problem at det blir flere eldre og fødes færre barn i Norge.",
        "Unge i dag bør velge å få færre barn for planetens skyld.",
        "Rike land som Norge har et spesielt ansvar for å hjelpe folk som må flytte på grunn av fattigdom eller klima.",
        "Vi må være forberedt på å ta imot flere flyktninger på grunn av klimaendringer i andre deler av verden.",
        "Innvandring er nødvendig for at Norge skal ha nok folk til å gjøre viktige jobber i fremtiden.",
        "Det kan ikke bli en bærekraftig verden så lenge noen er superrike og andre er ekstremt fattige.",
        "Det er politikernes og de store selskapenes ansvar å fikse klimakrisen, ikke mitt.",
        "Ny teknologi vil løse de fleste av verdens bærekraftsproblemer for oss.",
        "Å spise mat som er produsert lokalt er viktigere enn å spise økologisk mat fra et annet land.",
        "Det burde være mye dyrere å spise kjøtt.",
        "Alle burde ha rett til å fly på ferie minst én gang i året, uansett miljøkostnad.",
        "Vi bør ofre litt av vår egen velstand i Norge for å bidra til en mer rettferdig verden.",
        "Fremtidige generasjoner kommer til å ha det verre enn oss på grunn av måten vi lever på i dag.",
        "Å velge en 'miljøvennlig' karriere er viktigere enn å velge en jobb man tjener mye penger på.",
        "Det er viktigere å bevare naturen enn å skape nye arbeidsplasser.",
        "Norge burde slutte å lete etter mer olje og gass umiddelbart, selv om landet da tjener mindre penger.",
        "Det er egoistisk å bygge vindmøller som skader norsk natur, kun for å selge strøm til utlandet.",
        "Atomkraft er en skummel, men nødvendig løsning på klimakrisen.",
        "Elbiler løser ingen problemer, de bare flytter forurensningen til landene der batteriene lages.",
        "Å streame serier og game i timevis er et skjult miljøproblem som ingen snakker om.",
        "Foreldrene våre har et ansvar for å kjøpe bærekraftige produkter, selv om det er dyrere.",
        "Det burde være en lov som tvinger produsenter til å lage ting (som mobiler) som er enkle å reparere.",
        "Å kaste fullt spiselig mat burde vært straffbart.",
        "\"Best før\"-datoen på mat er bare tull og bør fjernes for å redusere matsvinn.",
        "Man kan ikke kalle seg en ekte miljøforkjemper hvis man ikke er vegetarianer eller veganer.",
        "Klimaaktivister som skulker skolen for å demonstrere, gjør en viktigere jobb enn det de lærer i klasserommet.",
        "Å poste om klima på sosiale medier er en lat måte å være aktivist på og gjør ingen reell forskjell.",
        "Det er umulig for én person å leve 100% bærekraftig i dagens samfunn.",
        "Å dusje i mer enn fem minutter er egoistisk.",
        "Skolen har et ansvar for å servere bærekraftig mat i kantina.",
        "Å redde en utrydningstruet dyreart er viktigere enn å bygge en ny vei eller et nytt boligfelt.",
        "Turister bør betale en egen \"naturskatt\" for å besøke populære steder i Norge, som Lofoten eller Preikestolen.",
        "Det er urettferdig at fattige land må kutte sine utslipp når rike land har forurenset fritt i hundrevis av år.",
        "Vi i Norge bør fokusere på å løse våre egne miljøproblemer før vi kritiserer andre land.",
        "Det viktigste målet med bærekraft er å utrydde fattigdom, ikke å kutte klimagassutslipp."
      ]
    }
  };

  const opinionScale = [
    { key: 'strongly_disagree', label: 'Helt uenig', color: '#ef4444' },
    { key: 'disagree', label: 'Litt uenig', color: '#f87171' },
    { key: 'agree', label: 'Litt enig', color: '#4ade80' },
    { key: 'strongly_agree', label: 'Helt enig', color: '#22c55e' }
  ];

  const startMethods = [
    "Hvem var først på skolen i dag?",
    "Hvem la seg først i går kveld?",
    "Hvem har lengst hår?",
    "Hvem har flest søsken?",
    "Hvem har bursdag nærmest i tid?",
    "Hvem har størst sko?",
    "Hvem står opp tidligst på hverdager?",
    "Hvem har vært lengst borte fra hjemstedet sitt?",
    "Hvem spiste frokost først i dag?",
    "Hvem har flest husdyr?"
  ];

   const generatePlayers = () => {
  const playerList = [];
  for (let i = 1; i <= numPlayers; i++) {
    playerList.push({ id: i, name: `Spiller ${i}`, number: i });
  }
  setPlayers(playerList);

  // NY LOGIKK: Velger start-metode med en gang
  const randomMethod = startMethods[Math.floor(Math.random() * startMethods.length)];
  setStartMethod(randomMethod);
  setStartingPlayerSelected(false); // Tilbakestiller for hvert nye spill

  setGameState('playing');
};
  
 const handleCategorySelect = (name) => {
    setOriginalCategory(name); // Lagre den valgte hovedkategorien
    setGameState('confirm-warmup'); // Gå til den nye skjermen
  };

  const drawStatement = (categoryToDrawFrom) => {
    // Fjerner den gamle linjen: let categoryToDrawFrom = selectedCategory;
    
    const statements = categories[categoryToDrawFrom].statements;
    let categoryUsed = usedStatements[categoryToDrawFrom] || [];
    const availableStatements = statements.filter(statement => !categoryUsed.includes(statement));
    
    let randomStatement;
    if (availableStatements.length > 0) {
      randomStatement = availableStatements[Math.floor(Math.random() * availableStatements.length)];
      setUsedStatements(prev => ({ ...prev, [categoryToDrawFrom]: [...categoryUsed, randomStatement] }));
    } else {
      randomStatement = statements[Math.floor(Math.random() * statements.length)];
      setUsedStatements(prev => ({ ...prev, [categoryToDrawFrom]: [randomStatement] }));
    }
    
    setCurrentStatement(randomStatement);
    setPositions({});
    setCurrentPlayer(0);
    setRound(prev => prev + 1);
    setShowArchive(false);
    setGameState('statement');
  };

  const registerOpinion = (opinion) => {
    const player = players[currentPlayer];
    const newPositions = { ...positions, [player.id]: opinion };
    setPositions(newPositions);
    
    if (currentPlayer < players.length - 1) {
      setCurrentPlayer(prev => prev + 1);
    } else {
      setCurrentPlayer(0);
      setDiscussedStatements(prev => [...prev, {
        statement: currentStatement,
        category: selectedCategory,
        round: round,
        responses: newPositions
      }]);

      // NY LOGIKK: Sjekker om oppvarmingen er ferdig
      if (isWarmupPhase && remainingWarmups - 1 === 0) {
        setGameState('warmup-complete'); // Gå til ny overgangsskjerm
      } else if (isWarmupPhase) {
        setRemainingWarmups(prev => prev - 1); // Fortsett oppvarmingen
      }
    }
  };
  
  const changeCategory = () => {
    if (isWarmupPhase) return; // Kan ikke bytte kategori under oppvarming
    setSelectedCategory(null);
    setGameState('playing');
    setShowArchive(false);
  };

  const getRemainingStatements = () => {
    if (!selectedCategory) return 0;
    const statements = categories[selectedCategory].statements;
    const categoryUsed = usedStatements[selectedCategory] || [];
    return statements.length - categoryUsed.length;
  };

  const resetGame = () => {
    setGameState('setup');
    setPlayers([]);
    setNumPlayers(3);
    setSelectedCategory(null);
    setCurrentPlayer(0);
    setCurrentStatement(null);
    setPositions({});
    setRound(1);
    setStartMethod(null);
    setUsedStatements({});
    setDiscussedStatements([]);
    setShowArchive(false);
    setRemainingWarmups(0);
    setOriginalCategory(null);
    setIsWarmupPhase(false);
    setStartingPlayerSelected(false);
  };
  
const handlePlayerNameChange = (id, newName) => {
    setPlayers(prevPlayers =>
      prevPlayers.map(player =>
        player.id === id ? { ...player, name: newName } : player
      )
    );
  };

  const selectStartingPlayer = (selectedPlayerId) => {
    if (startingPlayerSelected) return; // Ikke gjør noe hvis en spiller allerede er valgt

    const selectedPlayer = players.find(p => p.id === selectedPlayerId);
    const otherPlayers = players.filter(p => p.id !== selectedPlayerId);
    
    // Setter den valgte spilleren først i listen
    const reorderedPlayers = [selectedPlayer, ...otherPlayers];

    setPlayers(reorderedPlayers);
    setStartingPlayerSelected(true); // Marker at startspiller er valgt
  };

  const Archive = () => (
    <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 15px 35px rgba(0,0,0,0.1)', padding: '32px', marginTop: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          📝 Arkiv ({discussedStatements.length})
        </h3>
        <button onClick={() => setShowArchive(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>
          ✕
        </button>
      </div>
      
      <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
        {discussedStatements.slice().reverse().map((item, index) => (
          <div key={index} style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '16px', marginBottom: '16px', borderLeft: `6px solid ${categories[item.category]?.color || '#6b7280'}`, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: categories[item.category]?.color || '#6b7280', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Runde {item.round}</span>
              <span>•</span>
              <span>{item.category}</span>
            </div>
            <div style={{ fontSize: '18px', color: '#1f2937', marginBottom: '16px', lineHeight: '1.4', fontWeight: '500' }}>
              "{item.statement}"
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {Object.entries(item.responses).map(([playerId, response]) => {
                const player = players.find(p => p.id === parseInt(playerId));
                const opinionData = opinionScale.find(op => op.key === response);
                return (
                  <span key={playerId} style={{ fontSize: '13px', padding: '6px 12px', borderRadius: '20px', backgroundColor: opinionData?.color || '#6b7280', color: 'white', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {player?.name}: {opinionData?.label}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

if (gameState === 'setup') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{ fontSize: '48px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
              Dialogspill
            </h1>
            <p style={{ color: '#6b7280' }}>
              Et spill for å fremme diskusjon og meningsutveksling
            </p>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', padding: '32px 32px 24px 32px', marginBottom: '24px', textAlign: 'center' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>
              Hvor mange spillere?
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '32px' }}>
              <button onClick={() => setNumPlayers(Math.max(2, numPlayers - 1))} style={{ backgroundColor: '#f3f4f6', color: '#4b5563', width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '24px' }}>
                -
              </button>
              <span style={{ fontSize: '48px', fontWeight: 'bold', color: '#1f2937', width: '80px', textAlign: 'center' }}>
                {numPlayers}
              </span>
              <button onClick={() => setNumPlayers(Math.min(8, numPlayers + 1))} style={{ backgroundColor: '#f3f4f6', color: '#4b5563', width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '24px' }}>
                +
              </button>
            </div>
            <button onClick={generatePlayers} style={{ width: '100%', backgroundColor: '#22c55e', color: 'white', padding: '16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>
              Start spill
            </button>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', padding: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
              Slik fungerer spillet:
            </h3>
            <ol style={{ paddingLeft: '20px', color: '#4b5563', lineHeight: '1.6' }}>
              <li style={{ marginBottom: '8px' }}>Velg antall spillere.</li>
              <li style={{ marginBottom: '8px' }}>Velg en kategori for diskusjonen.</li>
              <li style={{ marginBottom: '8px' }}>Ta stilling til om dere ønsker en kort oppvarming først.</li>
              <li style={{ marginBottom: '8px' }}>Følg instruksjonene og start diskusjonen!</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }
  
 if (gameState === 'playing') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)', padding: '16px' }}>
        <style>
          {`
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
            @keyframes borderGlow { 0% { background: linear-gradient(45deg, #3b82f6, #8b5cf6, #3b82f6); } 33% { background: linear-gradient(45deg, #8b5cf6, #ec4899, #8b5cf6); } 66% { background: linear-gradient(45deg, #ec4899, #3b82f6, #ec4899); } 100% { background: linear-gradient(45deg, #3b82f6, #8b5cf6, #3b82f6); } }
          `}
        </style>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
              Dialogspill - Runde {round}
            </h1>
            <div style={{ display: 'flex', gap: '8px' }}>
                            {discussedStatements.length > 0 && (
                <button onClick={() => setShowArchive(!showArchive)} style={{ backgroundColor: showArchive ? '#059669' : '#3b82f6', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                  📝 Arkiv ({discussedStatements.length})
                </button>
              )}
              <button onClick={resetGame} style={{ backgroundColor: '#6b7280', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <Home style={{ marginRight: '8px' }} size={16} /> Nytt spill
              </button>
            </div>
          </div>

          {!startingPlayerSelected && startMethod && (
  <div style={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '16px', padding: '24px', marginBottom: '24px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
    <h3 style={{ fontWeight: 'bold', color: '#92400e', margin: '0 0 8px 0', fontSize: '24px' }}>Hvem starter?</h3>
    <p style={{ color: '#b45309', margin: '0 0 12px 0', fontSize: '18px', fontStyle: 'italic' }}>"{startMethod}"</p>
    <p style={{ color: '#92400e', margin: 0, fontWeight: 'bold' }}>Trykk på spilleren det gjelder for å starte.</p>
  </div>
)}

<div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.15)', padding: '32px', marginBottom: '24px' }}>
    <h2 style={{ fontSize: '32px', fontWeight: 'bold', textAlign: 'center', marginBottom: '24px', color: '#1f2937' }}> Spillere </h2>
    
    {/* Vises kun FØR startspiller er valgt */}
    {!startingPlayerSelected && (
        <p style={{ textAlign: 'center', marginTop: '-16px', marginBottom: '24px', color: '#4b5563' }}>
            Tips: Skriv inn deres egne navn i boksene under!
        </p>
    )}

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
        {players.map((player, index) => {
            // Logikk for rammen rundt spillerkortet
            const isStarter = startingPlayerSelected && index === 0;
            let borderStyle = '3px solid transparent'; // Standard er ingen ramme
            if (isStarter) {
                borderStyle = '3px solid #22c55e'; // Grønn ramme for den som starter
            } else if (!startingPlayerSelected) {
                borderStyle = `3px dashed ${index === 0 ? '#3b82f6' : '#d1d5db'}`; // Prikket ramme før valg
            }

            return (
                <div 
                    key={player.id} 
                    onClick={() => selectStartingPlayer(player.id)}
                    style={{ 
                        padding: '24px', 
                        borderRadius: '16px', 
                        transition: 'all 0.3s ease', 
                        background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', 
                        color: '#374151', 
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        cursor: startingPlayerSelected ? 'default' : 'pointer',
                        border: borderStyle,
                        transform: !startingPlayerSelected && index === 0 ? 'scale(1.05)' : 'scale(1)'
                    }}
                >
                    <div style={{ textAlign: 'center' }}>
                        <input
                            type="text"
                            value={player.name}
                            onChange={(e) => handlePlayerNameChange(player.id, e.target.value)}
                            disabled={startingPlayerSelected}
                            placeholder="Skriv inn navn"
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'center',
                                width: '100%',
                                color: '#1f2937',
                                padding: '8px',
                                borderRadius: '8px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            );
        })}
    </div>
</div>
          
          <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.15)', padding: '32px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center', color: '#1f2937' }}>
              Velg kategori
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
              {Object.entries(categories)
                .filter(([name]) => name !== 'Oppvarming')
                .map(([name, category]) => (
                <button key={name} onClick={() => handleCategorySelect(name)} style={{ backgroundColor: category.color, color: 'white', padding: '32px', borderRadius: '16px', border: 'none', cursor: 'pointer', transition: 'transform 0.2s ease' }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}> {name} </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}> {category.statements.length} påstander </div>
                </button>
              ))}
            </div>
          </div>

          {showArchive && <Archive />}
        </div>
      </div>
    );
  }

 if (gameState === 'confirm-warmup') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          maxWidth: '600px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
        }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
            🔥 Ønsker du en oppvarming?
          </h1>
          <p style={{ color: '#4b5563', marginBottom: '24px', fontSize: '16px' }}>
            Hovedkategori: <strong style={{ color: categories[originalCategory]?.color || '#1f2937' }}>{originalCategory}</strong>
          </p>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', color: '#6b7280', marginBottom: '12px', fontWeight: '500', fontSize: '14px' }}>Ja, start med:</label>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              {[1, 3, 5].map(num => (
                <button
                  key={num}
                  onClick={() => {
                    setIsWarmupPhase(true);
                    setRemainingWarmups(num);
                    setSelectedCategory('Oppvarming');
                    drawStatement('Oppvarming'); // Sender kategorinavnet direkte
                  }}
                  style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '2px solid #eab308', padding: '10px 20px', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'transform 0.2s ease' }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {num} runde{num > 1 ? 'r' : ''}
                </button>
              ))}
            </div>
          </div>

          <div style={{ margin: '20px 0', borderTop: '1px solid #e5e7eb' }}></div>

          <button
            onClick={() => {
              setIsWarmupPhase(false);
              setSelectedCategory(originalCategory);
              drawStatement(originalCategory); // Sender kategorinavnet direkte
            }}
            style={{ width: '100%', backgroundColor: '#f3f4f6', color: '#4b5563', border: '2px solid #d1d5db', padding: '12px', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Nei, start direkte med hovedkategorien
          </button>
        </div>
      </div>
    );
  }

  // LIM INN DENNE NYE BLOKKEN
  if (gameState === 'warmup-complete') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          maxWidth: '600px',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
        }}>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
            🔥 Oppvarming fullført!
          </h1>
          <p style={{ color: '#4b5563', marginBottom: '32px', fontSize: '18px' }}>
            Nå starter hovedspillet med kategorien: <br />
            <strong style={{ color: categories[originalCategory]?.color || '#1f2937' }}>{originalCategory}</strong>
          </p>
          <button
            onClick={() => {
              setIsWarmupPhase(false);
              setSelectedCategory(originalCategory);
              setOriginalCategory(null);
              drawStatement(originalCategory); // KORREKSJON: Starter spillet direkte
            }}
            
            style={{
              width: '100%',
              backgroundColor: '#22c55e',
              color: 'white',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            Start hovedspill
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'statement') {
    const currentPlayerData = players[currentPlayer];
    const hasEveryoneAnswered = players.length > 0 && players.every(player => positions[player.id]);

    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)', padding: '16px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}> Runde {round} </h1>
            <div style={{ display: 'flex', gap: '8px' }}>
                {discussedStatements.length > 0 && (
                    <button onClick={() => setShowArchive(!showArchive)} style={{ backgroundColor: showArchive ? '#059669' : '#3b82f6', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                        📝 Arkiv
                    </button>
                )}
                <button onClick={() => setGameState('playing')} style={{ backgroundColor: '#6b7280', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                    Tilbake til kategorier
                </button>
            </div>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '20px', boxShadow: '0 30px 60px rgba(0,0,0,0.25)', padding: '40px', marginBottom: '32px', border: `3px solid ${categories[selectedCategory]?.color || '#3b82f6'}`, position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-3px', left: '-3px', right: '-3px', bottom: '-3px', background: 'linear-gradient(45deg, #3b82f6, #8b5cf6, #3b82f6)', borderRadius: '20px', zIndex: '-1', animation: 'borderGlow 3s ease infinite' }}></div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ display: 'inline-block', padding: '16px 32px', borderRadius: '30px', color: 'white', fontWeight: 'bold', backgroundColor: categories[selectedCategory]?.color || '#3b82f6', marginBottom: '16px', fontSize: '20px', boxShadow: '0 8px 25px rgba(0,0,0,0.2)', position: 'relative' }}>
                {selectedCategory}
                <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#fbbf24', color: '#92400e', fontSize: '12px', fontWeight: 'bold', padding: '4px 8px', borderRadius: '12px' }}>
                  {getRemainingStatements()} igjen
                </span>
              </div>
              <h2 style={{ fontSize: '42px', fontWeight: 'bold', color: '#1f2937', marginBottom: '32px', lineHeight: '1.2', textShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '20px', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '16px', border: '2px solid #e2e8f0' }}>
                "{currentStatement}"
              </h2>
            </div>

            {!hasEveryoneAnswered ? (
              <div>
                <div style={{ backgroundColor: '#dbeafe', border: '2px solid #3b82f6', borderRadius: '16px', padding: '20px', marginBottom: '32px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e40af', marginBottom: '4px' }}> {currentPlayerData?.name || ''} sin tur </div>
                  <div style={{ color: '#2563eb', fontSize: '16px' }}> Les påstanden høyt og velg din mening </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '20px', maxWidth: '600px', margin: '0 auto' }}>
                  {opinionScale.map((opinion) => (
                    <button
                      key={opinion.key}
                      onClick={() => registerOpinion(opinion.key)}
                      style={{ backgroundColor: opinion.color, color: 'white', padding: '40px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', textAlign: 'center', boxShadow: '0 15px 35px rgba(0,0,0,0.2)', fontSize: '20px', fontWeight: 'bold', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.3)', background: `linear-gradient(135deg, ${opinion.color}, ${opinion.color}dd)` }}
                      onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05) translateY(-3px)'; e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.25)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1) translateY(0)'; e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.2)'; }}
                    >
                      {opinion.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : ( // Dette er "else"-delen
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '16px', color: '#059669' }}> 🎉 Alle har svart! </h3>
                <p style={{ color: '#6b7280', marginBottom: '32px', fontSize: '18px' }}> Nå kan dere diskutere. Trykk på en av knappene under for å fortsette.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px', margin: '0 auto' }}>
                  <button onClick={() => drawStatement(selectedCategory)} style={{ width: '100%', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: 'white', padding: '16px', borderRadius: '16px', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)' }}>
                    <Shuffle style={{ marginRight: '12px' }} size={20} /> 
                    {isWarmupPhase ? `Neste oppvarmingsrunde (${remainingWarmups} igjen)` : 'Trekk ny påstand'}
                  </button>
                  {!isWarmupPhase && (
                    <button onClick={changeCategory} style={{ width: '100%', background: 'linear-gradient(135deg, #6b7280, #4b5563)', color: 'white', padding: '12px', borderRadius: '16px', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(107, 114, 128, 0.3)' }}>
                      Bytt kategori
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.15)', padding: '32px', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#1f2937' }}> Svar så langt: </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {players.map((player) => {
                const position = positions[player.id];
                const opinionData = position ? opinionScale.find(op => op.key === position) : null;
                return (
                  <div key={player.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '16px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#1f2937' }}> {player.name} </span>
                    {opinionData ? (
                      <span style={{ padding: '8px 16px', borderRadius: '24px', color: 'white', fontWeight: 'bold', backgroundColor: opinionData.color, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                        {opinionData.label}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontWeight: 'bold' }}> Venter på svar... </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {showArchive && <Archive />}
        </div>
      </div>
    );
  }
};

export default DialogGame;